import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { ChainAddress } from '@/utils/models';
import { resolveDefaultTokenPair } from '@/utils/swap';

type SyncUrlParameters = (
  buyAddress?: ChainAddress,
  buyChain?: number,
  sellAddress?: ChainAddress,
  sellChain?: number,
) => void;

type UseInitializeDefaultTokensParams = {
  buyTokenAddress: ChainAddress | undefined;
  buyTokenChainId: number | undefined;
  sellTokenAddress: ChainAddress | undefined;
  sellTokenChainId: number | undefined;
  /**
   * Writes the resolved token query parameters to the URL. Each consumer
   * decides how to render them (e.g. token pages ignore the buy side because it
   * is the page token).
   */
  syncUrlParameters: SyncUrlParameters;
};

/**
 * Ensures the trade module never renders with an empty token field by filling
 * the empty side(s) with the static default pair (see ADR 0001). The selection
 * is independent of the connected wallet and its balances.
 *
 * - Both sides empty: fill with the fixed Ethereum USDC -> ETH pair.
 * - Exactly one side empty (token pages, deep links): fill the empty side on
 *   the present side's chain, flipping to the counterpart's default on
 *   collision (e.g. a USDC page becomes native -> USDC).
 * - Both sides present: leave the pair alone.
 */
export const useInitializeDefaultTokens = ({
  buyTokenAddress,
  buyTokenChainId,
  sellTokenAddress,
  sellTokenChainId,
  syncUrlParameters,
}: UseInitializeDefaultTokensParams) => {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const resolved = resolveDefaultTokenPair({
      buyTokenAddress,
      buyTokenChainId,
      sellTokenAddress,
      sellTokenChainId,
    });

    if (
      resolved.buyTokenAddress === buyTokenAddress &&
      resolved.buyTokenChainId === buyTokenChainId &&
      resolved.sellTokenAddress === sellTokenAddress &&
      resolved.sellTokenChainId === sellTokenChainId
    )
      return;

    syncUrlParameters(
      resolved.buyTokenAddress,
      resolved.buyTokenChainId,
      resolved.sellTokenAddress,
      resolved.sellTokenChainId,
    );
  }, [
    router.isReady,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    syncUrlParameters,
  ]);
};
